/**
 * 「布置听写」表单的**纯逻辑**（选词条件 → 请求体、输入校验、条件快照 → 人话）。
 *
 * 为什么单独一个文件：这一页有两个"看起来能用其实不对"的地方，而它们都很适合
 * 用纯函数锁住：
 *
 *   1. **年级永远显式传**。第一步的取词接口在 `gradeCodes` 为空时会回落到
 *      "这个孩子的年级" —— 界面显示"一个年级都没选（= 全部年级）"、实际却按某个
 *      年级取词，是最难查的那种不一致（与 `DictationPickPanel` 同一条理由）。
 *      所以"全不选"= 显式给**全部年级**。
 *   2. **条件快照要说人话**。会话把 `pickCriteria` 存了下来（后端补全默认值后的
 *      `applied`），历史列表要把它渲染成一句家长能看懂的话 —— 这句话只能有一份实现，
 *      否则"布置时的说明"与"历史里的说明"会漂移成两种说法。
 */
import {
  GRADE_CODES,
  DEFAULT_DICTATION_TEMPO,
  gradeLabel,
  type DictationTempoConfig,
} from '@fht/shared';
import { semesterLabel, wordCategoryLabel } from './api/dictation';

/** 「布置听写」表单的全部状态。 */
export interface BuildDraft {
  /** 选中的年级（**空数组 = 不限年级**，会在发请求时展开成全部年级）。 */
  gradeCodes: string[];
  semesters: string[];
  categories: string[];
  fromTextbookOnly: boolean;
  /** 随机取几个（字符串，留空 = 按顺序全取）。 */
  randomCount: string;
  /**
   * 取词来源（第四步）。
   *
   *   · `all` —— 全部词；
   *   · `wrong_first` —— **错词优先**：先排错词（按下次该复习的时间），
   *     剩余名额补这个范围内的新词；
   *   · `wrong` —— 只听写错词。
   *
   * ⚠️ `wrong` / `wrong_first` 与 `randomCount` 的"随机"**不能同时生效**：
   *   错词的顺序是"最该复习的排最前"（确定性的），随机化会让那句话失效。
   *   所以界面在选了错词来源时**明确提示**随机取数不参与排序。
   */
  source: 'all' | 'wrong_first' | 'wrong';
  title: string;
  tempo: DictationTempoConfig;
  /**
   * 家长动过节奏没有。
   * `false` = 还没动过 → 允许用"上次用过的节奏"预填（见页面里那一段）。
   * ⚠️ 少了这个标志就会出现"家长刚调好、被异步返回的默认值覆盖掉"。
   */
  tempoTouched: boolean;
}

/** 新建草稿的初值。 */
export function initialBuildDraft(): BuildDraft {
  return {
    gradeCodes: [],
    semesters: [],
    categories: [],
    fromTextbookOnly: false,
    randomCount: '20',
    source: 'all',
    title: '',
    tempo: { ...DEFAULT_DICTATION_TEMPO },
    tempoTouched: false,
  };
}

/**
 * 选定年级 → 真正发给后端的 `gradeCodes`。
 *
 * ⚠️ **永远不返回空数组**：空了后端会回落到"这个孩子的年级"，而界面显示的是
 * "不限年级"。所以"全不选"= 显式给全部年级。
 */
export function gradeCodesForRequest(selected: readonly string[]): string[] {
  const known = selected.filter((code) => (GRADE_CODES as readonly string[]).includes(code));
  return known.length > 0 ? [...known] : [...GRADE_CODES];
}

/**
 * 解析"随机取几个"。
 *
 * 返回 `null` 表示"没填/不合法" —— 调用方据此决定是"按顺序全取"还是报错。
 * 刻意**不**在这里静默成 0 或 20：那会让家长输错一个数就得到一份不是他要的听写。
 */
export function parseRandomCount(raw: string): number | null {
  const text = raw.trim();
  if (text === '') return null;
  const value = Number(text);
  if (!Number.isInteger(value) || value < 1 || value > 200) return null;
  return value;
}

/** 表单校验：返回第一处问题（人话）或 `ok`。 */
export function validateBuildDraft(
  draft: BuildDraft,
): { ok: true } | { ok: false; message: string } {
  if (draft.tempo.repeatCount < 1) {
    return { ok: false, message: '「每个词读几遍」至少是 1 遍。' };
  }
  const random = draft.randomCount.trim();
  if (random !== '' && parseRandomCount(random) === null) {
    return { ok: false, message: '「随机取几个」要填 1~200 之间的整数，或者留空。' };
  }
  return { ok: true };
}

/**
 * 请求体里的 `pick`（原样透传给后端的 `pickDictationWords`）。
 *
 * ⚠️ `source` 现在**会**被带上（第四步接上了错词）。第三步时它被刻意省略
 *    （那时传 `source='wrong'` 会被后端 501 拒），现在不是了。
 *
 * ⚠️ 选了错词来源时**不传 `randomCount`**：错词的顺序是
 *    "最该复习的排最前"（确定性），随机化会让界面上那句话失效。
 *    `limit` 仍然传 —— 它决定"最多取几个"。
 */
export function buildPickPayload(draft: BuildDraft): Record<string, unknown> {
  const random = parseRandomCount(draft.randomCount);
  const payload: Record<string, unknown> = {
    gradeCodes: gradeCodesForRequest(draft.gradeCodes),
    source: draft.source,
  };
  if (draft.semesters.length > 0) payload['semesters'] = [...draft.semesters];
  if (draft.categories.length > 0) payload['categories'] = [...draft.categories];
  if (draft.fromTextbookOnly) payload['fromTextbookOnly'] = true;

  if (draft.source === 'all') {
    if (random !== null) {
      payload['randomCount'] = random;
      /**
       * 同时给 `limit`：不随机时它决定"最多取几个"，随机时后端用 `randomCount`，
       * 但两个都给能让 `applied` 快照里看到明确的上限（界面上"这次取了什么"更实在）。
       */
      payload['limit'] = random;
    }
  } else if (random !== null) {
    /** 错词来源：只要上限，不要随机。 */
    payload['limit'] = random;
  }

  // 拼音要一起取：界面上要显示读音（显示用，朗读仍然靠 TTS/录音）
  payload['includePinyin'] = true;
  return payload;
}

/** 从后端给的 `applied` 里读一个字符串数组；拿不到就是空数组。 */
function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

/**
 * 选词条件快照 → 一句人话（历史列表与回看页都用它）。
 *
 * 输入是后端 `applied` 的快照，字段可能缺失（老数据 / 手工改过的行）——
 * 缺什么就按**默认值**说（"全部年级 · 全部学期 · 按文字顺序"），
 * 而不是渲染一行空白：空白在界面上看起来像页面坏了。
 */
export function describeAppliedCriteria(criteria: Record<string, unknown>): string {
  const parts: string[] = [];

  const grades = stringList(criteria['gradeCodes']);
  if (grades.length === 0 || GRADE_CODES.every((code) => grades.includes(code))) {
    parts.push('全部年级');
  } else {
    parts.push(grades.map((code) => gradeLabel(code)).join('、'));
  }

  const semesters = stringList(criteria['semesters']);
  parts.push(
    semesters.length === 0 ? '全部学期' : semesters.map((item) => semesterLabel(item)).join('、'),
  );

  const categories = stringList(criteria['categories']);
  if (categories.length > 0) {
    parts.push(categories.map((item) => wordCategoryLabel(item)).join('、'));
  }

  if (criteria['fromTextbookOnly'] === true) parts.push('只看课本词语表');

  const randomCount = criteria['randomCount'];
  if (typeof randomCount === 'number' && Number.isFinite(randomCount)) {
    parts.push(`随机 ${randomCount} 个`);
  } else {
    parts.push('按文字顺序');
  }

  /**
   * 第四步：来源。`wrong_first` / `wrong` 时把**排序规则**也说出来 ——
   * 否则历史列表里只看到"错词优先"四个字，家长不知道那几个词的顺序是怎么来的。
   */
  const source = criteria['source'];
  if (source === 'wrong_first') parts.push('错词优先（错词按该复习的时间排前，再补新词）');
  else if (source === 'wrong') parts.push('只听写错词（按该复习的时间排）');

  const lessonIds = stringList(criteria['lessonIds']);
  if (lessonIds.length > 0) parts.push(`指定了 ${lessonIds.length} 节课`);

  return parts.join(' · ');
}

/**
 * 从教师/家长的角度说"这次听写是怎么来的"（回看页顶部那句）。
 *
 * 与 `describeAppliedCriteria` 的分工：那个只讲**取词条件**，这个把**节奏**也带上，
 * 用于"进去看题目"时的一行摘要。
 */
export function describeSessionOrigin(
  criteria: Record<string, unknown>,
  tempo: DictationTempoConfig,
): string {
  return `${describeAppliedCriteria(criteria)} · 每个词读 ${tempo.repeatCount} 遍、间隔 ${
    tempo.gapSeconds
  } 秒、换词再停 ${tempo.betweenWordsSeconds} 秒`;
}

/**
 * 「照样再来一次」：把上一次会话的**选词条件快照**翻译回表单状态。
 *
 * ## 为什么需要它（而不是让家长重新选一遍）
 *
 * 用户最常做的一件事是"昨天那份听写再来一遍"。而条件快照里存的是**后端补全默认值
 * 之后的 `applied`**，与表单的字段名并不一一对应（例如 `gradeCodes` 里会是
 * "全部 12 个年级"，那种情况应当还原成表单上的"一个都不选 = 不限年级"）。
 *
 * ⚠️ **不还原词本身**：会话里那 20 个词是**当天随机取到的结果**，照抄它们不是
 *    "再来一次"而是"考同一张卷子"。家长的意图是"同样的范围、再来一组"，
 *    所以这里只还原**条件**，让后端重新取（要复现同一组词就带 `seed`，
 *    那个入口在取词预览里）。
 *
 * 缺字段就用默认值（老数据 / 手工改过的行都可能有）。
 */
export function draftFromCriteria(
  criteria: Record<string, unknown>,
  base: BuildDraft = initialBuildDraft(),
): BuildDraft {
  const grades = stringList(criteria['gradeCodes']);
  const allGrades = GRADE_CODES.every((code) => grades.includes(code));
  const randomCount = criteria['randomCount'];
  const semesters = stringList(criteria['semesters']);
  const categories = stringList(criteria['categories']);

  return {
    ...base,
    // "全部年级" 在表单上的表达就是"一个都不选"
    gradeCodes: grades.length === 0 || allGrades ? [] : grades,
    semesters,
    categories,
    fromTextbookOnly: criteria['fromTextbookOnly'] === true,
    /** 来源也还原（"昨天那份错词复习再来一次"要能照做）。认不出来的回到 `all`。 */
    source:
      criteria['source'] === 'wrong_first'
        ? 'wrong_first'
        : criteria['source'] === 'wrong'
          ? 'wrong'
          : 'all',
    randomCount:
      typeof randomCount === 'number' && Number.isFinite(randomCount)
        ? String(randomCount)
        : base.randomCount,
    /**
     * 标题**不沿用**上一次的：新的一次听写应当是今天的名字（后端也会按今天生成）。
     * 照抄旧标题会让历史列表里出现两条一模一样的名字，谁也分不清哪个是哪个。
     */
    title: '',
    /**
     * 节奏**沿用**上一次（那正是家长上次调好的数），并且标记成"动过" ——
     * 否则异步返回的"上次节奏"会把它覆盖成同一份值，白白闪一下。
     */
    tempo: { ...base.tempo },
    tempoTouched: true,
  };
}
